import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single axios instance with cookies enabled (httpOnly JWT)
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Inject Bearer fallback for environments where 3rd-party cookies are blocked
// + cache-buster on GET to ensure fresh data after mutations
api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('gd-access-token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {}
  if ((config.method || 'get').toLowerCase() === 'get') {
    config.params = { ...(config.params || {}), _: Date.now() };
    config.headers['Cache-Control'] = 'no-cache';
  }
  return config;
});

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || 'Erreur inconnue.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Auth
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
};

// Users
export const usersApi = {
  list: () => api.get('/users'),
  create: (payload) => api.post('/users', payload),
  update: (id, payload) => api.patch(`/users/${id}`, payload),
  remove: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, temp_password) =>
    api.post(`/users/${id}/reset-password`, { temp_password }),
  agents: () => api.get('/agents'),
};

// Tenants
export const tenantsApi = {
  list: () => api.get('/tenants'),
  create: (payload) => api.post('/tenants', payload),
  update: (id, payload) => api.patch(`/tenants/${id}`, payload),
  remove: (id) => api.delete(`/tenants/${id}`),
  myTenant: () => api.get('/my-tenant'),
};

// Reference
export const refApi = {
  regions: (country_mode) => api.get('/regions', { params: { country_mode } }),
  communes: (country_mode) => api.get('/communes', { params: { country_mode } }),
  infraTypes: (country_mode) => api.get('/infrastructure-types', { params: { country_mode } }),
  infrastructures: (country_mode, commune_id) =>
    api.get('/infrastructures', { params: { country_mode, commune_id } }),
};

// Signalements
export const signalApi = {
  list: ({ country_mode, commune_id, limit } = {}) =>
    api.get('/signalements', { params: { country_mode, commune_id, limit } }),
  create: (payload) => api.post('/signalements', payload),
  update: (id, payload) => api.patch(`/signalements/${id}`, payload),
  photo: (id) => api.get(`/signalements/${id}/photo`),
};

// Public (signalement citoyen, sans authentification)
export const publicApi = {
  communes: (country_mode) => api.get('/public/communes', { params: { country_mode } }),
  commune: (id) => api.get(`/public/commune/${id}`),
  infraTypes: (country_mode) => api.get('/public/infrastructure-types', { params: { country_mode } }),
  createSignalement: (payload) => api.post('/public/signalements', payload),
};

// Stats
export const statsApi = {
  summary: (country_mode, commune_id) => api.get('/stats/summary', { params: { country_mode, commune_id } }),
};

// Admin
export const adminApi = {
  clearMapData: (payload) => api.post('/admin/clear-map-data', payload),
};

export default api;
